// redux/slices/qrcodeSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { resetStore } from "./resetStore";
import { getServerUrl, getAuthHeaders } from "../../Utils/serverSelector";

// helper: абсолютный URL + cache-busting
const abs = (p) => {
  if (!p) return null;
  const base = (getServerUrl() || "").replace(/\/+$/, "");
  const rel = String(p).replace(/^\/+/, "");
  const url = `${base}/${rel}`;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}t=${Date.now()}`;
};

export const generateQRCode = createAsyncThunk(
  "qrcode/generateQRCode",
  async ({ uid, masterLink }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID not provided");

      const baseUrl = getServerUrl();
      if (!baseUrl) throw new Error("API server URL is not configured");

      const authHeaders = await getAuthHeaders();

      const res = await axios.post(
        `${baseUrl}/qrcode`,
        { uid, masterLink },
        {
          headers: authHeaders,
          withCredentials: true,          // ⬅️ важное место
          validateStatus: () => true,
        }
      );

      if (res.status !== 200 && res.status !== 201) {
        throw new Error(res.data?.message || `Failed to generate QR (HTTP ${res.status})`);
      }

      return abs(res?.data?.filePath);
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to generate QR");
    }
  }
);

export const fetchQRCode = createAsyncThunk(
  "qrcode/fetchQRCode",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID not provided");

      const baseUrl = getServerUrl();
      if (!baseUrl) throw new Error("API server URL is not configured");

      const authHeaders = await getAuthHeaders();

      const res = await axios.get(`${baseUrl}/qrcode/${uid}/qrcode`, {
        headers: authHeaders,
        withCredentials: true,           // ⬅️ тоже обязательно
        validateStatus: () => true,
      });

      // если файл ещё не создавался — 404, считаем это "фото нет"
      if (res.status === 404) return null;

      if (res.status === 403) {
        // тут как раз был твой кейс — теперь, если JWT/кука ок, этого быть не должно
        throw new Error(res.data?.message || "Forbidden (403)");
      }

      if (res.status !== 200) {
        throw new Error(res.data?.message || `Failed to fetch QR (HTTP ${res.status})`);
      }

      if (res?.data?.filePath) return abs(res.data.filePath);
      return null;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch QR");
    }
  }
);

export const deleteQRCode = createAsyncThunk(
  "qrcode/deleteQRCode",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID not provided");

      const baseUrl = getServerUrl();
      if (!baseUrl) throw new Error("API server URL is not configured");

      const authHeaders = await getAuthHeaders();

      const res = await axios.delete(`${baseUrl}/qrcode/${uid}`, {
        headers: authHeaders,
        withCredentials: true,           // ⬅️ и здесь
        validateStatus: () => true,
      });

      if (res.status !== 200) {
        throw new Error(res.data?.message || `Failed to delete QR (HTTP ${res.status})`);
      }

      return null;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to delete QR");
    }
  }
);

const initialState = {
  qrCodeUrl: null,
  status: "idle", // idle | loading | failed
  error: null,
};

const qrcodeSlice = createSlice({
  name: "qrcode",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // generate
      .addCase(generateQRCode.pending, (s) => {
        s.status = "loading";
        s.error = null;
      })
      .addCase(generateQRCode.fulfilled, (s, a) => {
        s.status = "idle";
        s.qrCodeUrl = a.payload;
      })
      .addCase(generateQRCode.rejected, (s, a) => {
        s.status = "failed";
        s.error = a.payload;
      })

      // fetch
      .addCase(fetchQRCode.pending, (s) => {
        s.status = "loading";
        s.error = null;
      })
      .addCase(fetchQRCode.fulfilled, (s, a) => {
        s.status = "idle";
        s.qrCodeUrl = a.payload; // может быть null — это ок
      })
      .addCase(fetchQRCode.rejected, (s, a) => {
        s.status = "failed";
        s.error = a.payload;
      })

      // delete
      .addCase(deleteQRCode.pending, (s) => {
        s.status = "loading";
        s.error = null;
      })
      .addCase(deleteQRCode.fulfilled, (s) => {
        s.status = "idle";
        s.qrCodeUrl = null;
      })
      .addCase(deleteQRCode.rejected, (s, a) => {
        s.status = "failed";
        s.error = a.payload;
      })

      // reset store
      .addCase(resetStore, () => initialState);
  },
});

export default qrcodeSlice.reducer;
